package com.hyperether.goodjob.database

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.hyperether.goodjob.models.Step
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow

class JobDaoImpl(private val sharedDatabase: SharedDatabase) : JobDao {

    override suspend fun insertJob(job: com.hyperether.goodjob.models.Job) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.insertJob(
                    _id = job.id,
                    jobTitle = job.jobTitle,
                    status = job.status,
                    assignees = Mapper().objectAsJson(job.assignees),
                    creatorId = job.creatorId,
                    workspaceId = job.workspaceId,
                    start_at = job.start_at,
                    end_at = job.end_at,
                    repeat = job.repeat,
                    repeatInterval = Mapper().objectAsJson(job.repeatInterval),
                    startDate = job.startDate,
                    dueDate = job.dueDate,
                    jobDuration = job.jobDuration,
                    steps = Mapper().listAsJsonSteps(job.steps),
                    userId = job.userId,
                    contactId = job.contactSiteId,
                    contactSiteName = job.contactSiteName,
                    contactParentId = job.contactParentId,
                    notes = Mapper().listAsJsonNotes(job.noteObjects),
                    fileObjects = Mapper().listAsJson(job.fileObjects),
                    completionReport = job.completionReport
                )
            }
        }
    }

    override suspend fun updateJobById(job: com.hyperether.goodjob.models.Job) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.updateJobByID(
                    _id = job.id,
                    id = job.id,
                    jobTitle = job.jobTitle,
                    status = job.status,
                    assignees = Mapper().objectAsJson(job.assignees),
                    creatorId = job.creatorId,
                    workspaceId = job.workspaceId,
                    start_at = job.start_at,
                    end_at = job.end_at,
                    repeat = job.repeat,
                    repeatInterval = Mapper().objectAsJson(job.repeatInterval),
                    startDate = job.startDate,
                    dueDate = job.dueDate,
                    jobDuration = job.jobDuration,
                    steps = Mapper().listAsJsonSteps(job.steps),
                    userId = job.userId,
                    contactId = job.contactSiteId,
                    contactSiteName = job.contactSiteName,
                    contactParentId = job.contactParentId,
                    notes = Mapper().listAsJsonNotes(job.noteObjects),
                    fileObjects = Mapper().listAsJson(job.fileObjects),
                    completionReport = job.completionReport
                )
            }
        }
    }


    override suspend fun getJobsFlow(): Flow<List<Job>> {
        return sharedDatabase { database ->
            database.appDatabaseQueries
                .getJobs()
                .asFlow()
                .mapToList(Dispatchers.Default)
        }
    }

    override suspend fun getJobByIdFlow(jobId: String): Flow<Job?> {
        return sharedDatabase { database ->
            database.appDatabaseQueries
                .getJobById(jobId)
                .asFlow()
                .mapToOneOrNull(Dispatchers.Default)
        }
    }

    override suspend fun getJobById(jobId: String): com.hyperether.goodjob.models.Job? =
        sharedDatabase { database ->
            database.appDatabaseQueries
                .getJobById(jobId)
                .executeAsOneOrNull()
                ?.let { Mapper().toJob(it) }
        }

    override suspend fun getJobsFromLocal(): List<Job> =
        sharedDatabase { database ->
            database.appDatabaseQueries.getJobs().awaitAsList()
        }

    override suspend fun deleteJobById(id: String) {
        sharedDatabase { database ->
            database.appDatabaseQueries.deleteJobById(id)
        }
    }

    override suspend fun deleteAllJobs() {
        sharedDatabase { database ->
            database.appDatabaseQueries.deleteAllJobs()
        }
    }

    override suspend fun getJobsFromLocal(workspaceId: String): List<Job> =
        sharedDatabase { database ->
            database.appDatabaseQueries.getJobsByWorkspaceId(workspaceId).awaitAsList()
        }

    override suspend fun getJobsByContactIdFlow(id: String): Flow<List<Job>> {
        return sharedDatabase { database ->
            database.appDatabaseQueries
                .getJobsByContactId(id)
                .asFlow()
                .mapToList(Dispatchers.Default)
        }
    }

    override suspend fun updateJobStatusById(jobId: String, newStatus: String) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.updateJobStatusById(
                    id = jobId,
                    status = newStatus
                )
            }
        }
    }

    override suspend fun updateJobCompletionReport(
        jobId: String,
        report: CompletionReportResponse
    ) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.updateJobCompletionReport(
                    id = jobId,
                    completionReport = Mapper().objectAsJson(report)
                )
            }
        }
    }

    override suspend fun getJobCompletionReport(jobId: String): CompletionReportResponse? {
        return sharedDatabase { database ->
            val job = database.appDatabaseQueries.getJobById(jobId).executeAsOneOrNull()
            job?.completionReport?.let { reportJson ->
                try {
                    Mapper().jsonAsObject<CompletionReportResponse>(reportJson)
                } catch (e: Exception) {
                    null
                }
            }
        }
    }

    override suspend fun updateFiles(jobId: String, files: List<String>) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.updateFiles(
                    id = jobId,
                    files = Mapper().listAsJson(files)
                )
            }
        }
    }

    override suspend fun updateJobSteps(jobId: String, steps: List<Step>) {
        sharedDatabase { database ->
            database.appDatabaseQueries.transaction {
                database.appDatabaseQueries.updateSteps(
                    steps = Mapper().listAsJson(steps),
                    id = jobId
                )
            }
        }
    }
}